/* ---------- PALETTE TOKENS ----------
   Three accents are load-bearing: a1/a2/a3 map to the three age stages
   (balloon / beach ball / volleyball) and become the program's age coding
   everywhere else. If two read alike at a glance, the palette fails. */

:root {
  /* Palette: Meadow. a1/a2/a3 are the vivid fills (balloon, borders, dots);
     the -ink variants are darkened for label text, which needs 4.5:1. */
  --bg: #F1F7F0; --surface: #FFFFFF; --edge: #DBE8D9;
  --ink: #1C2B21; --ink-soft: #55685B;
  --a1: #94D9AE; --a2: #BCA9E8; --a3: #E89A9A;
  --a1-ink: #217049; --a2-ink: #6A4EB6; --a3-ink: #A93232;
  --btn-fg: #FFFFFF;

  --display: 'Fredoka', 'Trebuchet MS', sans-serif;
        --body: 'IBM Plex Sans', system-ui, sans-serif;
        --mono: 'IBM Plex Mono', monospace; }

* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body {
  margin: 0; background: var(--bg); color: var(--ink);
  font-family: var(--body); font-size: 17px; line-height: 1.6;
  transition: background .35s ease, color .35s ease;
  -webkit-font-smoothing: antialiased;
}
.wrap { max-width: 1080px; margin: 0 auto; padding: 0 24px; }
.eyebrow { font-family: var(--mono); font-size: 12px; letter-spacing: .14em;
  text-transform: uppercase; color: var(--ink-soft); margin: 0 0 14px; }

/* ---------- MASTHEAD + HERO ---------- */
.mark { display: inline-flex; align-items: center; gap: 10px; padding-top: 26px;
  font-family: var(--display); font-weight: 600; font-size: 20px; }
/* Stacked lockup, portrait 0.91:1 - needs real height or the "Children's
   Volleyball" line stops being legible. Genuinely transparent, so no
   blend-mode trick required. */
.mark-img { display: block; height: 116px; width: auto; margin-top: 22px; }
.hero { display: grid; grid-template-columns: 1.05fr .95fr; gap: 44px;
  align-items: center; padding: 46px 0 68px; }
.hero h1 { font-family: var(--display); font-weight: 600;
  font-size: clamp(38px, 6vw, 68px); line-height: 1.02; letter-spacing: -.02em; margin: 0 0 20px; }
.hero h1 em { font-style: normal; color: var(--a1); }
.lede { font-size: clamp(17px, 1.9vw, 20px); color: var(--ink-soft); margin: 0 0 30px; max-width: 44ch; }
.lede strong { color: var(--ink); font-weight: 600; }
.cta-row { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
.cta-note { font-family: var(--mono); font-size: 12.5px; color: var(--ink-soft); }

.btn { display: inline-block; font-family: var(--display); font-weight: 600; font-size: 17px;
  background: var(--ink); color: var(--btn-fg); border: 0; border-radius: 999px;
  padding: 15px 30px; cursor: pointer; text-decoration: none;
  transition: transform .18s ease, background .18s ease; }
.btn:hover { background: var(--a3); transform: translateY(-2px); }
.btn:focus-visible { outline: 3px solid var(--a1); outline-offset: 3px; }
.btn:disabled { opacity: .55; cursor: not-allowed; transform: none; }

/* ---------- HERO DIAGRAM ---------- */
.diagram { width: 100%; height: auto; overflow: visible; }
.arc { stroke: var(--ink-soft); stroke-width: 2; fill: none;
  stroke-dasharray: 6 9; stroke-linecap: round; opacity: .45; }
.floor { stroke: var(--edge); stroke-width: 3; stroke-linecap: round; }
.dlabel { font-family: var(--mono); font-size: 11px; fill: var(--ink-soft); letter-spacing: .08em; }
.float { animation: drift 5.2s ease-in-out infinite; }
.shad { animation: shrink 5.2s ease-in-out infinite; transform-origin: 180px 296px; }
@keyframes drift { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
@keyframes shrink { 0%,100% { transform: scaleX(1); opacity: .5; } 50% { transform: scaleX(.86); opacity: .3; } }

/* ---------- STAGES ---------- */
.section-head { max-width: 60ch; margin: 0 0 38px; }
.section-head h2 { font-family: var(--display); font-weight: 600;
  font-size: clamp(28px, 3.6vw, 40px); line-height: 1.1; letter-spacing: -.015em; margin: 0 0 12px; }
.section-head p { color: var(--ink-soft); margin: 0; }
.stages { padding: 0 0 72px; }
.stage-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.stage { background: var(--surface); border-radius: 22px; padding: 26px 24px 28px;
  border-top: 6px solid var(--c); transition: background .35s ease; }
.stage:nth-child(1) { --c: var(--a1); --c-ink: var(--a1-ink); }
.stage:nth-child(2) { --c: var(--a2); --c-ink: var(--a2-ink); }
.stage:nth-child(3) { --c: var(--a3); --c-ink: var(--a3-ink); }
.ages { font-family: var(--mono); font-size: 12px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--c-ink); margin: 0 0 6px; font-weight: 500; }
.stage h3 { font-family: var(--display); font-weight: 600; font-size: 22px; margin: 0 0 10px; }
.stage p { margin: 0 0 15px; font-size: 15.5px; color: var(--ink-soft); }
.skills { list-style: none; margin: 0; padding: 0; font-size: 14.5px; }
.skills li { padding: 7px 0 7px 22px; position: relative; border-top: 1px solid var(--bg); }
.skills li::before { content: ''; position: absolute; left: 0; top: 14px;
  width: 9px; height: 9px; border-radius: 50%; background: var(--c); }

/* ---------- WAITLIST ---------- */
.waitlist { padding: 0 0 84px; }
.card { background: var(--surface); border-radius: 26px; padding: clamp(28px, 4vw, 50px);
  display: grid; grid-template-columns: .9fr 1.1fr; gap: 42px; align-items: start;
  transition: background .35s ease; }
.card h2 { font-family: var(--display); font-weight: 600;
  font-size: clamp(26px, 3.2vw, 36px); line-height: 1.1; margin: 0 0 14px; }
.pitch { color: var(--ink-soft); margin: 0 0 18px; }
.privacy { font-size: 13.5px; color: var(--ink-soft);
  border-left: 3px solid var(--edge); padding-left: 14px; margin: 0; }

.field { margin: 0 0 20px; }
.field label, .legend { display: block; font-family: var(--mono); font-size: 11.5px;
  letter-spacing: .12em; text-transform: uppercase; color: var(--ink-soft); margin: 0 0 8px; }
.hint { text-transform: none; letter-spacing: 0; font-family: var(--body);
  font-size: 13px; display: block; margin-top: 4px; }
.field input { width: 100%; font-family: var(--body); font-size: 16px; color: var(--ink);
  padding: 13px 16px; border: 2px solid var(--edge); border-radius: 12px; background: var(--bg); }
.field input:focus-visible { outline: 3px solid var(--a3); outline-offset: 1px; border-color: transparent; }
fieldset { border: 0; padding: 0; margin: 0 0 20px; }
.chips { display: flex; flex-wrap: wrap; gap: 9px; }
.chip { padding: 10px 16px; border-radius: 999px; border: 2px solid var(--edge);
  font-size: 14.5px; cursor: pointer; background: transparent; color: var(--ink);
  font-family: var(--body); transition: all .15s ease; }
.chip[aria-pressed='true'] { background: var(--ink); border-color: var(--ink); color: var(--btn-fg); }
.chip:focus-visible { outline: 3px solid var(--a3); outline-offset: 2px; }
.child-row { border: 2px solid var(--edge); border-radius: 14px; padding: 16px 16px 4px;
  margin: 0 0 14px; background: var(--surface); }
.child-row .legend { display: flex; align-items: baseline; justify-content: space-between;
  gap: 12px; }
.child-row .field { margin: 0 0 14px; }
.link-btn { background: none; border: 0; padding: 0; cursor: pointer; color: var(--a2-ink);
  font-family: var(--mono); font-size: 11.5px; letter-spacing: .12em;
  text-transform: uppercase; text-decoration: underline; }
.link-btn:focus-visible { outline: 3px solid var(--a3); outline-offset: 2px; border-radius: 4px; }
.add-child { display: block; margin: 0 0 20px; }

.err { color: var(--a3-ink); font-size: 14px; margin: 0 0 14px; font-weight: 600; }
.success h3 { font-family: var(--display); font-weight: 600; font-size: 26px; margin: 0 0 10px; }
.success p { color: var(--ink-soft); margin: 0; }

footer { border-top: 1px solid var(--edge); padding: 24px 0 40px; }
.foot-row { display: flex; flex-wrap: wrap; gap: 16px; justify-content: space-between;
  align-items: center; font-family: var(--mono); font-size: 12px; color: var(--ink-soft); }
footer a { color: var(--ink); }

@media (max-width: 900px) {
  .hero { grid-template-columns: 1fr; gap: 24px; padding: 32px 0 52px; }
  .hero .dwrap { order: -1; max-width: 360px; }
  .stage-grid { grid-template-columns: 1fr; }
  .card { grid-template-columns: 1fr; gap: 28px; }
}
@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  .float, .shad { animation: none; }
  * { transition: none !important; }
}

/* ---------- BALLOON TAP ---------- */
/* Vertical position is driven by the rAF physics loop writing an inline
   transform on the wrapper group. This animation only does the squash, so the
   two never fight over the same transform. */
.squash {
  transform-box: fill-box;
  transform-origin: center center;
  animation: squash .26s cubic-bezier(.22, 1, .36, 1);
}
@keyframes squash {
  0%   { transform: scale(1, 1); }
  35%  { transform: scale(1.12, .88); }
  100% { transform: scale(1, 1); }
}

/* Once play starts, the idle drift and its shadow pulse must stop or they
   compound with the physics transform. */
.diagram.playing .float,
.diagram.playing .shad { animation: none; }
.shad { transform-box: fill-box; transform-origin: center center; }

.cheer { animation: cheer-in .4s cubic-bezier(.22, 1, .36, 1); }
@keyframes cheer-in {
  0%   { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}
.cheer-big {
  font-family: var(--display); font-weight: 600; font-size: 34px;
  fill: var(--a3); letter-spacing: -.01em;
}
.cheer-sub {
  font-family: var(--mono); font-size: 13px; fill: var(--ink-soft); letter-spacing: .1em;
}
/* outline on an SVG element draws a rectangle around the bbox and cannot
   follow the balloon's shape, so suppress it and draw the ring in SVG. */
.balloon-hit { cursor: pointer; -webkit-tap-highlight-color: transparent; }
.balloon-hit, .balloon-hit * { user-select: none; }
.balloon-hit:focus { outline: none; }
.balloon-hit .focus-ring {
  fill: none; stroke: var(--a3); stroke-width: 3;
  opacity: 0; pointer-events: none; transition: opacity .15s ease;
}
.balloon-hit:focus-visible .focus-ring { opacity: 1; }
@supports not selector(:focus-visible) {
  .balloon-hit:focus .focus-ring { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .bounce, .shad-duck { animation: none; }
}
